import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Plus, X } from 'lucide-react'
import type { Connector, ConnectorType } from '@/types'
import { CONNECTOR_LABELS, CONNECTOR_DESCRIPTIONS, isAiProviderConnector } from './connectorConstants'
import { CONNECTOR_ICONS } from './connectorIcons'

interface ConnectorCardProps {
  connectorType: ConnectorType
  connector: Connector | undefined
  onClick: () => void
  canManage: boolean
  isConnectedOverride?: boolean
  unavailableReason?: string
}

export function ConnectorCard({
  connectorType,
  connector,
  onClick,
  canManage,
  isConnectedOverride,
  unavailableReason,
}: ConnectorCardProps) {
  const Icon = CONNECTOR_ICONS[connectorType]
  const isConnected = isConnectedOverride ?? connector !== undefined
  const hasUnavailableReason = !!unavailableReason
  const isUnavailable = !isConnected && hasUnavailableReason
  const unavailableReasonId = `connector-card-${connectorType}-unavailable-reason`

  return (
    <Card
      className={cn(
        'p-6 flex flex-col gap-4 transition-all',
        isUnavailable ? 'cursor-default' : 'cursor-pointer hover:bg-muted/50 hover:shadow-lg',
      )}
      data-testid={`connector-card-${connectorType}`}
      role="button"
      aria-label={`${CONNECTOR_LABELS[connectorType]} connector${isConnected ? ' (connected)' : ''}`}
      aria-disabled={isUnavailable || undefined}
      aria-describedby={hasUnavailableReason ? unavailableReasonId : undefined}
      onClick={isUnavailable ? undefined : onClick}
    >
      <div className={cn('flex items-start justify-between', isUnavailable && 'opacity-60')}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <h3 className="font-semibold">{CONNECTOR_LABELS[connectorType]}</h3>
            {isAiProviderConnector(connectorType) && (
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary">AI Provider</span>
            )}
          </div>
        </div>

        {canManage && !isUnavailable && (isConnected ? (
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground transition-colors">
            <X className="h-4 w-4" />
            <span>Disable</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs font-medium text-foreground transition-colors">
            <Plus className="h-4 w-4" />
            <span>Enable</span>
          </div>
        ))}
      </div>

      <p className="text-sm text-muted-foreground flex-1 line-clamp-2">
        {CONNECTOR_DESCRIPTIONS[connectorType]}
      </p>

      {hasUnavailableReason && (
        <p
          id={unavailableReasonId}
          className="self-start rounded-full border px-3 py-1 text-xs font-semibold text-muted-foreground"
          data-testid={`connector-card-${connectorType}-unavailable`}
        >
          {unavailableReason}
        </p>
      )}
    </Card>
  )
}
